import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { searchEvents } from "../../../services/eventsServices";

import {
  ensureArray,
  eventMatchesDateRange,
  normalizeEvent,
  normalizeSearchText,
  sortEvents,
} from "../utils/eventsUtils";

const INITIAL_FILTERS = {
  q: "",
  categorie: "",
  status: "upcoming",
  dateFrom: "",
  dateTo: "",
  limit: 12,
  sort: "start_date",
  dir: "ASC",
};

function extractSearchItems(response) {
  if (Array.isArray(response)) return response;
  return ensureArray(response?.items);
}

export function useEventsPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [allItems, setAllItems] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [expandedEvent, setExpandedEvent] = useState(null);

  const deferredSearch = useDeferredValue(filters.q);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await searchEvents({
        is_active: true,
        page: 1,
        limit: 500,
        sort: "start_date",
        dir: "ASC",
      });

      const rows = extractSearchItems(response);
      setAllItems(rows.map(normalizeEvent));
    } catch (err) {
      console.error("Erreur de chargement événements:", err);
      setAllItems([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    loadEvents();
  }, [loadEvents]);

  const categories = useMemo(() => {
    return Array.from(
      new Set(
        allItems
          .map((event) => event.category)
          .filter(Boolean)
          .map((category) => String(category).trim())
      )
    ).sort((a, b) => a.localeCompare(b, "fr"));
  }, [allItems]);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(deferredSearch);
    const selectedCategory = String(filters.categorie || "").trim();

    const filtered = allItems.filter((event) => {
      const matchSearch = q ? event.searchIndex.includes(q) : true;

      const matchCategory = selectedCategory
        ? event.category === selectedCategory
        : true;

      const matchStatus =
        filters.status === "all" ? true : event.status === filters.status;

      const matchDates = eventMatchesDateRange(
        event,
        filters.dateFrom,
        filters.dateTo
      );

      return matchSearch && matchCategory && matchStatus && matchDates;
    });

    return sortEvents(filtered, filters.sort, filters.dir);
  }, [
    allItems,
    deferredSearch,
    filters.categorie,
    filters.status,
    filters.dateFrom,
    filters.dateTo,
    filters.sort,
    filters.dir,
  ]);

  const total = filteredItems.length;

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / filters.limit), 1);
  }, [filters.limit, total]);

  const paginatedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * filters.limit;
    return filteredItems.slice(start, start + filters.limit);
  }, [filteredItems, filters.limit, page, totalPages]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const updateFilter = useCallback((name, value) => {
    setPage(1);
    setExpandedEvent(null);

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
    setExpandedEvent(null);
  }, []);

  const toggleCategory = useCallback((category) => {
    setPage(1);
    setExpandedEvent(null);

    setFilters((previous) => ({
      ...previous,
      categorie: previous.categorie === category ? "" : category,
    }));
  }, []);

  const setLimit = useCallback((limit) => {
    setPage(1);
    setFilters((previous) => ({
      ...previous,
      limit: Number(limit) || 12,
    }));
  }, []);

  const setSort = useCallback((sort) => {
    setPage(1);
    setFilters((previous) => ({
      ...previous,
      sort,
    }));
  }, []);

  const setDir = useCallback((dir) => {
    setPage(1);
    setFilters((previous) => ({
      ...previous,
      dir,
    }));
  }, []);

  const goToPrevPage = useCallback(() => {
    setPage((current) => Math.max(current - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((current) => Math.min(current + 1, totalPages));
  }, [totalPages]);

  const openEvent = useCallback((event) => {
    setExpandedEvent(event);
  }, []);

  const closeEvent = useCallback(() => {
    setExpandedEvent(null);
  }, []);

  const refresh = useCallback(() => {
    setExpandedEvent(null);
    loadEvents();
  }, [loadEvents]);

  return {
    loading,
    error,

    items: filteredItems,
    paginatedItems,
    total,
    allTotal: allItems.length,
    totalPages,

    categories,
    filters,
    page,

    expandedEvent,

    updateFilter,
    resetFilters,
    toggleCategory,
    setLimit,
    setSort,
    setDir,
    goToPrevPage,
    goToNextPage,
    refresh,
    openEvent,
    closeEvent,
  };
}